import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { getCostsServices, getCostsServicesDetails } from '../lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Server, MapPin, Hash } from 'lucide-react';
import { RegionMap } from '../components/dashboard/RegionMap';

const PROVIDER_COLORS = {
  aws: '#FF9900',
  azure: '#007FFF',
  gcp: '#4285F4',
  ovh: '#123F6D'
};

const PROVIDER_NAMES = {
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP',
  ovh: 'OVH'
};

const RANGES = [
  { key: 1, label: '1 mois' },
  { key: 3, label: '3 mois' },
  { key: 6, label: '6 mois' },
  { key: 12, label: '12 mois' },
  { key: 24, label: '24 mois' },
];

const truncate = (str, max) => str.length > max ? str.slice(0, max) + '\u2026' : str;

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-popover/95 backdrop-blur-sm border border-border shadow-xl rounded-lg p-3 text-sm max-w-xs">
        <p className="font-medium break-words">{data.service}</p>
        <p className="text-muted-foreground mt-1">
          {data.cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € ({data.percentage}%)
        </p>
      </div>
    );
  }
  return null;
};

const renderBarLabel = (props) => {
  const { x, y, width, height, value } = props;
  if (width < 40) return null;
  return (
    <text x={x + width - 6} y={y + height / 2} fill="white" fontSize={11} fontWeight={500} textAnchor="end" dominantBaseline="middle">
      {value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
    </text>
  );
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

export const ServicesPage = () => {
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(1);
  const [expandedServices, setExpandedServices] = useState({});
  const isMobile = useIsMobile();

  const toggleService = (provider, service) => {
    const key = `${provider}::${service}`;
    setExpandedServices(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [servicesRes, detailsRes] = await Promise.all([
          getCostsServices(range),
          getCostsServicesDetails(range),
        ]);
        setData(servicesRes.data);
        setDetails(detailsRes.data);
      } catch (error) {
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [range]);

  const rangeLabel = RANGES.find(r => r.key === range)?.label || '';

  if (loading) {
    return (
      <Layout>
        <Header title="Coûts par Service" subtitle="Chargement..." />
        <Skeleton className="h-[500px]" />
      </Layout>
    );
  }

  const providers = data?.providers || [];

  return (
    <Layout>
      <Header title="Coûts par Service" subtitle={`Période : ${rangeLabel}`} />

      <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch gap-6 mt-6">
        <Card>
        <CardHeader className="flex-col sm:flex-row items-start sm:items-center justify-between gap-3 space-y-0 pb-2">
          <CardTitle className="text-lg font-[Outfit]">Services</CardTitle>
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1 w-full sm:w-auto">
            {RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={
                  'flex-1 sm:flex-initial px-3 py-1 text-xs font-medium rounded-md transition-colors ' +
                  (range === r.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground')
                }
              >
                {r.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {providers.length === 0 ? (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-muted-foreground">Aucune donnée de service disponible sur cette période.</p>
            </div>
          ) : (
            <Tabs defaultValue={providers[0]?.provider || 'aws'}>
              <TabsList className="mb-4 w-full sm:w-auto flex-wrap sm:flex-nowrap">
                {providers.map(p => (
                  <TabsTrigger key={p.provider} value={p.provider}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PROVIDER_COLORS[p.provider] }} />
                      {PROVIDER_NAMES[p.provider]}
                    </div>
                  </TabsTrigger>
                ))}
              </TabsList>
              {providers.map(provider => {
                const services = (provider.cost_by_service || []).map(s => ({
                  ...s,
                  shortName: truncate(s.service, 35),
                }));
                const barHeight = Math.max(services.length * 44, 150);
                const providerColor = PROVIDER_COLORS[provider.provider];

                return (
                  <TabsContent key={provider.provider} value={provider.provider}>
                    <div className="flex items-center justify-between mb-4 px-1">
                      <span className="text-sm text-muted-foreground">{services.length} services</span>
                      <span className="text-sm font-medium">
                        Total : {provider.total_cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </span>
                    </div>

                    {/* Mobile: list view */}
                    {isMobile ? (
                      <div className="space-y-2">
                        {services.map((s, i) => {
                          const maxCost = services[0]?.cost || 1;
                          const pct = Math.max((s.cost / maxCost) * 100, 2);
                          return (
                            <div key={i} className="space-y-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm truncate flex-1" title={s.service}>{s.service}</span>
                                <span className="text-sm font-medium whitespace-nowrap">
                                  {s.cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${pct}%`, backgroundColor: providerColor, opacity: 1 - i * 0.06 }}
                                />
                              </div>
                              <p className="text-xs text-muted-foreground">{s.percentage}% du total</p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Desktop: bar chart */
                      <ResponsiveContainer width="100%" height={barHeight}>
                        <BarChart
                          data={services}
                          layout="vertical"
                          margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
                          <XAxis
                            type="number"
                            tickFormatter={(value) => value.toLocaleString('fr-FR') + ' €'}
                            tick={{ fontSize: 11 }}
                            className="text-muted-foreground"
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            type="category"
                            dataKey="shortName"
                            tick={{ fontSize: 12 }}
                            width={200}
                            className="text-muted-foreground"
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted) / 0.3)' }} />
                          <Bar dataKey="cost" radius={[0, 6, 6, 0]} maxBarSize={32}>
                            {services.map((entry, index) => (
                              <Cell key={index} fill={providerColor} fillOpacity={1 - index * 0.08} />
                            ))}
                            <LabelList dataKey="cost" content={renderBarLabel} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
        </CardContent>
        </Card>
        <RegionMap data={details} />
      </div>

      {/* Service Details Section */}
      {details?.providers?.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg font-[Outfit]">Détails des ressources</CardTitle>
            <p className="text-sm text-muted-foreground">
              Détail des ressources par service (machines virtuelles, stockage, bases de données, etc.)
            </p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={details.providers[0]?.provider || 'aws'}>
              <TabsList className="mb-4 w-full sm:w-auto flex-wrap sm:flex-nowrap">
                {details.providers.map(p => (
                  <TabsTrigger key={p.provider} value={p.provider}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PROVIDER_COLORS[p.provider] }} />
                      {PROVIDER_NAMES[p.provider]}
                    </div>
                  </TabsTrigger>
                ))}
              </TabsList>
              {details.providers.map(provider => (
                <TabsContent key={provider.provider} value={provider.provider}>
                  <div className="space-y-2">
                    {provider.services.map(svc => {
                      const isExpanded = expandedServices[`${provider.provider}::${svc.service}`];
                      return (
                        <div key={svc.service} className="border border-border rounded-lg overflow-hidden">
                          {/* Service row header */}
                          <button
                            onClick={() => toggleService(provider.provider, svc.service)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                            data-testid={`service-toggle-${svc.service}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                              )}
                              <Server className="h-4 w-4 shrink-0" style={{ color: PROVIDER_COLORS[provider.provider] }} />
                              <span className="font-medium text-sm truncate">{svc.service}</span>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <Badge variant="secondary" className="text-xs">
                                {svc.resource_count} ressource{svc.resource_count > 1 ? 's' : ''}
                              </Badge>
                              <span className="text-sm font-semibold whitespace-nowrap">
                                {svc.total_cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                              </span>
                            </div>
                          </button>

                          {/* Expanded resource details */}
                          {isExpanded && (
                            <div className="border-t border-border bg-muted/20">
                              {isMobile ? (
                                /* Mobile: card list */
                                <div className="p-3 space-y-2">
                                  {svc.resources.map((res, i) => (
                                    <div key={i} className="bg-background border border-border rounded-md p-3 space-y-1.5">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium truncate">
                                          {res.resource_id || 'N/A'}
                                        </span>
                                        <span className="text-sm font-semibold whitespace-nowrap">
                                          {res.cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                                        </span>
                                      </div>
                                      {res.region && (
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                          <MapPin className="h-3 w-3" />
                                          {res.region}
                                        </div>
                                      )}
                                      {res.metadata && Object.keys(res.metadata).length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {Object.entries(res.metadata).slice(0, 4).map(([k, v]) => (
                                            <Badge key={k} variant="outline" className="text-[10px] font-normal">
                                              {k}: {String(v)}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                /* Desktop: table */
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                      <TableHead className="pl-12">Ressource</TableHead>
                                      <TableHead>Région</TableHead>
                                      <TableHead>Détails</TableHead>
                                      <TableHead className="text-right">Coût</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {svc.resources.map((res, i) => (
                                      <TableRow key={i}>
                                        <TableCell className="pl-12">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                            <span className="truncate max-w-[300px] font-mono text-xs" title={res.resource_id || 'N/A'}>
                                              {res.resource_id || 'N/A'}
                                            </span>
                                          </div>
                                        </TableCell>
                                        <TableCell>
                                          {res.region ? (
                                            <div className="flex items-center gap-1.5 text-sm">
                                              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                                              {res.region}
                                            </div>
                                          ) : (
                                            <span className="text-muted-foreground text-sm">—</span>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {res.metadata && Object.keys(res.metadata).length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                              {Object.entries(res.metadata).slice(0, 4).map(([k, v]) => (
                                                <Badge key={k} variant="outline" className="text-[10px] font-normal">
                                                  {k}: {String(v)}
                                                </Badge>
                                              ))}
                                            </div>
                                          ) : (
                                            <span className="text-muted-foreground text-sm">—</span>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-right font-medium whitespace-nowrap">
                                          {res.cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      )}
    </Layout>
  );
};
